import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import {
  VariantType,
  UIVariantType,
  UI_VARIANTS,
  UI_VARIANT_LABELS,
  uiVariantToDbKey,
  Tariff,
  TemplateVersion,
} from '../types';
import { tariffApi, templateApi } from '../api';
import { apiClient } from '../api/client';

type ImportSource = 'docusign' | 'tariff';

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  tariffId: number;
  variantType: VariantType;
  currentTemplateId?: string;
  onImportSuccess: () => void;
  currentTariffId: number;
  currentUiVariant: UIVariantType;
}

export function ImportDialog({
  open,
  onClose,
  tariffId,
  variantType,
  currentTemplateId,
  onImportSuccess,
  currentTariffId,
  currentUiVariant,
}: ImportDialogProps) {
  // DocuSign state
  const [docusignTemplateId, setDocusignTemplateId] = useState(currentTemplateId || '');

  // Common state
  const [importSource, setImportSource] = useState<ImportSource>('docusign');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Tariff browsing state
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [loadingTariffs, setLoadingTariffs] = useState(false);
  const [selectedTariffId, setSelectedTariffId] = useState<number | ''>('');
  const [selectedUiVariant, setSelectedUiVariant] = useState<UIVariantType | ''>('');
  const [selectedVersion, setSelectedVersion] = useState<TemplateVersion | null>(null);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Reset all state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setDocusignTemplateId(currentTemplateId || '');
      setImportSource('docusign');
      setError(null);
      setSuccess(null);
      resetTariffState();
    }
  }, [open, currentTemplateId]);

  const resetTariffState = () => {
    setSelectedTariffId('');
    setSelectedUiVariant('');
    setSelectedVersion(null);
    setVersions([]);
    setTariffs([]);
  };

  // Load tariffs when switching to tariff source
  useEffect(() => {
    if (importSource === 'tariff' && tariffs.length === 0) {
      loadTariffs();
    }
  }, [importSource]);

  const loadTariffs = async () => {
    setLoadingTariffs(true);
    try {
      const data = await tariffApi.listTariffs();
      setTariffs(data.tariffs);
    } catch (err) {
      setError('Failed to load tariffs');
    } finally {
      setLoadingTariffs(false);
    }
  };

  // Load versions when tariff + variant are selected
  useEffect(() => {
    if (selectedTariffId !== '' && selectedUiVariant !== '') {
      loadVersions(selectedTariffId, selectedUiVariant);
    } else {
      setVersions([]);
      setSelectedVersion(null);
    }
  }, [selectedTariffId, selectedUiVariant]);

  const loadVersions = async (tId: number, uiVar: UIVariantType) => {
    setLoadingVersions(true);
    setSelectedVersion(null);
    setVersions([]);
    try {
      const dbKey = uiVariantToDbKey(uiVar);
      const data = await templateApi.getTemplatesByTariffAndVariant(tId, dbKey);
      setVersions(data.templates.sort((a, b) => b.version - a.version));
    } catch (err: any) {
      // 404 means no templates exist — that's fine, show empty state
      if (err?.response?.status === 404 || err?.message?.includes('404')) {
        setVersions([]);
      } else {
        setError('Failed to load template versions');
      }
    } finally {
      setLoadingVersions(false);
    }
  };

  // Get available UI variants, excluding self
  const getAvailableVariants = (): UIVariantType[] => {
    return UI_VARIANTS.filter((v) => {
      if (selectedTariffId === currentTariffId) {
        return v !== currentUiVariant;
      }
      return true;
    });
  };

  const handleSourceChange = (_: React.MouseEvent<HTMLElement>, newSource: ImportSource | null) => {
    if (newSource !== null) {
      setImportSource(newSource);
      setError(null);
      setSuccess(null);
      if (newSource === 'docusign') {
        resetTariffState();
      }
    }
  };

  const handleTariffChange = (tId: number) => {
    setSelectedTariffId(tId);
    setSelectedUiVariant('');
    setSelectedVersion(null);
    setVersions([]);
  };

  const handleVariantChange = (uiVar: UIVariantType) => {
    setSelectedUiVariant(uiVar);
    setSelectedVersion(null);
  };

  const handleClose = () => {
    if (!importing) {
      setDocusignTemplateId('');
      setError(null);
      setSuccess(null);
      setImportSource('docusign');
      resetTariffState();
      onClose();
    }
  };

  const handleDocuSignImport = async () => {
    if (!docusignTemplateId.trim()) {
      setError('Please enter a DocuSign Template ID');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/templates/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          tariffId,
          variantType,
          docusignTemplateId: docusignTemplateId.trim(),
          userEmail: localStorage.getItem('user_email') || 'unknown@example.com',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
        throw new Error(errorData.error?.message || `Import failed: ${response.statusText}`);
      }

      const result = await response.json();
      setSuccess('Template imported successfully from DocuSign!');
      setError(null);

      setTimeout(() => {
        handleClose();
        onImportSuccess();
      }, 1500);

      console.log('DocuSign import successful:', result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to import template';
      setError(errorMessage);
      console.error('Import error:', err);
    } finally {
      setImporting(false);
    }
  };

  const handleTariffImport = async () => {
    if (!selectedVersion) {
      setError('Please select a template version to import');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const userEmail = localStorage.getItem('user_email') || 'unknown@example.com';
      const token = apiClient.getToken() || localStorage.getItem('auth_token');

      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/templates/import-from-tariff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tariffId,
          variantType,
          sourceTemplateId: selectedVersion.templateId,
          sourceVersion: selectedVersion.version,
          userEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
        throw new Error(errorData.error?.message || `Import failed: ${response.statusText}`);
      }

      const result = await response.json() as { success: boolean; templateId: string; version: number };

      setSuccess(`Template imported as version ${result.version}!`);
      setError(null);

      setTimeout(() => {
        handleClose();
        onImportSuccess();
      }, 1500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to import template from tariff';
      setError(errorMessage);
      console.error('Tariff import error:', err);
    } finally {
      setImporting(false);
    }
  };

  const handleImport = () => {
    if (importSource === 'docusign') {
      handleDocuSignImport();
    } else {
      handleTariffImport();
    }
  };

  const isImportDisabled = () => {
    if (importing) return true;
    if (importSource === 'docusign') return !docusignTemplateId.trim();
    return !selectedVersion;
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudDownloadIcon />
          Import Template
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Source Toggle */}
          <ToggleButtonGroup
            value={importSource}
            exclusive
            onChange={handleSourceChange}
            fullWidth
            size="small"
            disabled={importing}
          >
            <ToggleButton value="docusign">DocuSign</ToggleButton>
            <ToggleButton value="tariff">Tariff</ToggleButton>
          </ToggleButtonGroup>

          {/* DocuSign Import UI */}
          {importSource === 'docusign' && (
            <>
              <TextField
                label="DocuSign Template ID"
                value={docusignTemplateId}
                onChange={(e) => setDocusignTemplateId(e.target.value)}
                placeholder="e.g., abc-123-def-456"
                fullWidth
                required
                disabled={importing}
                helperText="Enter the template ID from DocuSign"
                autoFocus
              />
              <TextField label="Tariff ID" value={tariffId} disabled fullWidth />
              <TextField label="Variant Type" value={variantType} disabled fullWidth />
            </>
          )}

          {/* Tariff Import UI */}
          {importSource === 'tariff' && (
            <>
              {/* Tariff Select with search */}
              <Autocomplete
                options={tariffs}
                getOptionLabel={(t) => `${t.id} - ${t.VERSICHERUNG} ${t.TARIF}`}
                value={tariffs.find((t) => t.id === selectedTariffId) || null}
                onChange={(_, newValue) => {
                  handleTariffChange(newValue ? newValue.id : '' as any);
                }}
                disabled={importing || loadingTariffs}
                loading={loadingTariffs}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source Tariff"
                    placeholder="Type to search..."
                  />
                )}
                fullWidth
              />

              {/* Variant Select */}
              {selectedTariffId !== '' && (
                <FormControl fullWidth disabled={importing}>
                  <InputLabel>Source Variant</InputLabel>
                  <Select
                    value={selectedUiVariant}
                    onChange={(e) => handleVariantChange(e.target.value as UIVariantType)}
                    label="Source Variant"
                  >
                    {getAvailableVariants().map((v) => (
                      <MenuItem key={v} value={v}>
                        {UI_VARIANT_LABELS[v]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Version Select */}
              {selectedUiVariant !== '' && (
                <>
                  {loadingVersions ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading versions...
                      </Typography>
                    </Box>
                  ) : versions.length > 0 ? (
                    <FormControl fullWidth disabled={importing}>
                      <InputLabel>Source Version</InputLabel>
                      <Select
                        value={selectedVersion?.templateId && selectedVersion?.version
                          ? `${selectedVersion.templateId}::${selectedVersion.version}`
                          : ''}
                        onChange={(e) => {
                          const val = e.target.value as string;
                          const [tplId, ver] = val.split('::');
                          const found = versions.find(
                            (v) => v.templateId === tplId && v.version === Number(ver)
                          );
                          setSelectedVersion(found || null);
                        }}
                        label="Source Version"
                      >
                        {versions.map((v) => (
                          <MenuItem
                            key={`${v.templateId}::${v.version}`}
                            value={`${v.templateId}::${v.version}`}
                          >
                            v{v.version} — {v.status} — {formatDate(v.createdAt)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Alert severity="info">
                      No template versions found for this tariff/variant combination.
                    </Alert>
                  )}
                </>
              )}
            </>
          )}

          {/* Error / Success / Loading */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && <Alert severity="success">{success}</Alert>}

          {importing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {importSource === 'docusign'
                  ? 'Importing template from DocuSign...'
                  : 'Importing template from tariff...'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={isImportDisabled()}
          startIcon={importing ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
        >
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
